import { useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { fetchEventsListData, getSeededEventsListData } from '../data/eventsListApi'
import type { EventsListData } from '../data/types'
import type { FilterState, Sort } from '../data/filterState'

/** Owns the fetch lifecycle for the events list: re-fetches the full, already-filtered/sorted
 *  list (no pagination - the backend returns every matching event in one response) whenever the
 *  filter/sort state changes. Search text is debounced; every other filter change re-fetches
 *  immediately, same responsiveness as a checkbox/select naturally implies. */
export function useEventsListData(filters: FilterState, sort: Sort) {
  const [data, setData] = useState<EventsListData | null>(getSeededEventsListData)
  const [error, setError] = useState<string | null>(null)
  const requestId = useRef(0)
  // When the first render was already seeded with default-filter data (see
  // getSeededEventsListData), skip the mount-time fetch effect's first run below - it would
  // otherwise immediately re-fetch and overwrite data this page already has. Only the very first
  // effect run is skipped; any later filter/sort change re-fetches normally.
  const skipNextFetch = useRef(data !== null)

  useEffect(() => {
    const myId = ++requestId.current
    if (skipNextFetch.current) {
      skipNextFetch.current = false
      return
    }
    const debounceMs = filters.search ? 300 : 0
    const timer = setTimeout(() => {
      fetchEventsListData({
        search: filters.search,
        lat: filters.location?.lat ?? null,
        lng: filters.location?.lng ?? null,
        radiusMi: filters.location ? filters.radiusMi : null,
        when: filters.when,
        regState: filters.regState,
        raceType: filters.raceType,
        vehicleType: filters.vehicleType,
        seriesTrackId: filters.seriesTrackId,
        sort,
      })
        .then((d) => {
          if (requestId.current === myId) setData(d)
        })
        .catch((err: unknown) => {
          if (requestId.current !== myId) return
          Sentry.captureException(err, { tags: { component: 'events-list-react' } })
          setError(err instanceof Error ? err.message : String(err))
        })
    }, debounceMs)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filters is a single object recreated
    // on every change; stringify-free dependency tracking below keeps this effect correct.
  }, [
    filters.search,
    filters.location?.lat,
    filters.location?.lng,
    filters.radiusMi,
    filters.when,
    filters.regState.join(','),
    filters.raceType.join(','),
    filters.vehicleType.join(','),
    filters.seriesTrackId,
    sort,
  ])

  return { data, error }
}
